import { ApplicationRef, effect, inject, Injectable } from '@angular/core';
import { SwUpdate, VersionEvent, VersionReadyEvent } from '@angular/service-worker';
import { concat, first, interval } from 'rxjs';
import { buildInfo } from '../../../buildinfo';
import { environment } from '../../../environments/environment';
import { reloadPage } from '../utils/reload';
import { LoggerService } from './logger.service';
import { NetworkStateService } from './network-state.service';
import { ToastService } from './toast.service';
import { TranslationService } from './translation.service';

/**
 * Service Worker Update Manager
 *
 * Design principles:
 * ─────────────────────────────────────────────────────────────────────────────
 * 1. NO AUTOMATIC RELOADS — Every reload requires explicit user consent.
 *    Automatic reloads are highly disruptive in a local-first offline app
 *    where the user may be mid-session with unsaved state.
 *
 * 2. NGSW BEHAVIOUR NOTES:
 *    - NGSW calls skipWaiting() on `install` and clients.claim() on `activate`
 *      (hardcoded in @angular/service-worker). These cannot be disabled.
 *    - clients.claim() makes the new SW take over existing tabs without a page
 *      reload — but Safari PWA may reload the tab as a side-effect (known bug).
 *    - VERSION_READY fires once the new version is fully cached and activated.
 *    - UNRECOVERABLE fires when NGSW can't serve the app from its cache (e.g.
 *      after rapid deploys corrupt the cached manifest). We show a user-consent
 *      toast rather than silently reloading.
 *
 * 3. LOGGING — All SW lifecycle events are logged with version hashes so that
 *    the integration log stream shows exactly what state transitions occurred.
 */
@Injectable({ providedIn: 'root' })
export class SwUpdateService {
  private readonly appRef = inject(ApplicationRef);
  private readonly swUpdate = inject(SwUpdate);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslationService);
  private readonly networkState = inject(NetworkStateService);
  private readonly logger = inject(LoggerService).getLogger('sw-update');

  private updateCheckInterval = 30_000;
  private hasShownUpdateToast = false;
  private previousOfflineMode = this.networkState.stayOfflineMode();

  /**
   * Send a log line directly to the backend, bypassing the logger buffer.
   *
   * Uses fetch with keepalive:true — this survives page unload/reload and works
   * correctly in Safari. sendBeacon with application/json is NOT used because
   * Safari silently drops it (sendBeacon only supports CORS-safelisted content
   * types; application/json requires a preflight which sendBeacon never sends).
   */
  private beacon(message: string): void {
    const line = `[${new Date().toISOString()}] WARN  [SW] ${message}`;
    fetch(`${environment.apiUrl}/logs/client`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ logs: line }),
      keepalive: true,
    })?.catch(() => {
      /* best-effort — network may be unavailable */
    });
  }

  constructor() {
    // Always beacon build info + SW enabled state — this fires even when
    // swUpdate.isEnabled is false (e.g. UNRECOVERABLE state breaks the channel).
    this.beacon(
      `🔧 SW init: swEnabled=${this.swUpdate.isEnabled} ` +
        `build=${buildInfo.gitHash} @ ${buildInfo.timestamp}`,
    );
    this.logCurrentSwState();

    if (!this.swUpdate.isEnabled) {
      this.logger.debug('Service Worker updates are disabled (dev mode or UNRECOVERABLE)');
      this.beacon('SW updates disabled — skipping update listeners');
      return;
    }

    this.logger.info('🔧 Service Worker update manager initialised');
    this.logCurrentSwState();
    this.listenForControllerChange();
    this.setupUpdateChecks();
    this.listenForVersionEvents();
    this.listenForUnrecoverableErrors();
    this.watchOfflineModeChanges();
  }

  // ── Diagnostics ────────────────────────────────────────────────────────────

  /** Log the current SW registration state on startup for baseline visibility. */
  private logCurrentSwState(): void {
    if (!navigator.serviceWorker) return;
    navigator.serviceWorker
      .getRegistration()
      .then((reg) => {
        if (!reg) {
          this.beacon('SW state on startup: no registration found');
          this.logger.info('SW state: no registration found');
          return;
        }
        const state = `active=${reg.active?.scriptURL ?? 'none'} waiting=${reg.waiting?.scriptURL ?? 'none'} installing=${reg.installing?.scriptURL ?? 'none'} scope=${reg.scope}`;
        this.beacon(`SW registration state on startup: ${state}`);
        this.logger.info('SW registration state on startup:', {
          active: reg.active?.scriptURL ?? 'none',
          waiting: reg.waiting?.scriptURL ?? 'none',
          installing: reg.installing?.scriptURL ?? 'none',
          scope: reg.scope,
        });
      })
      .catch((err) => this.logger.error('SW getRegistration failed:', err));
  }

  /**
   * Listen for controllerchange — fired when clients.claim() swaps this page's
   * SW controller. Safari PWA is known to hard-reload the tab here (browser bug).
   * We log it so the event shows up in the backend log stream.
   */
  private listenForControllerChange(): void {
    if (!navigator.serviceWorker) return;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      const url = navigator.serviceWorker.controller?.scriptURL ?? 'none';
      // beacon first — page may reload immediately after this event in Safari PWA
      this.beacon(`⚠️ SW controllerchange fired (clients.claim) — new controller: ${url}`);
      this.logger.warn('⚠️ SW controller changed (clients.claim fired) — new controller:', url);
      // Do NOT reload here. User will be prompted via the VERSION_READY toast.
      // Safari PWA may auto-reload regardless — that is a known browser bug.
    });
  }

  // ── Update checks ──────────────────────────────────────────────────────────

  private watchOfflineModeChanges(): void {
    effect(() => {
      const current = this.networkState.stayOfflineMode();
      if (this.previousOfflineMode && !current) {
        this.logger.info('Offline mode disabled — checking for SW update');
        this.checkForUpdate().catch((err) =>
          this.logger.error('Update check failed after offline-mode change:', err),
        );
      }
      this.previousOfflineMode = current;
    });
  }

  private setupUpdateChecks(): void {
    const appIsStable$ = this.appRef.isStable.pipe(first((isStable) => isStable === true));
    const everyThirtySeconds$ = interval(this.updateCheckInterval);
    const everyThirtySecondsOnceAppIsStable$ = concat(appIsStable$, everyThirtySeconds$);

    everyThirtySecondsOnceAppIsStable$.subscribe(async () => {
      if (this.networkState.stayOfflineMode()) {
        this.logger.debug('Skipping update check — offline mode enabled');
        return;
      }
      try {
        const found = await this.swUpdate.checkForUpdate();
        this.logger.debug(
          found ? '🔍 Update check: new version found' : '🔍 Update check: already current',
        );
      } catch (err) {
        this.logger.error('Update check failed:', err);
      }
    });

    window.document.addEventListener('visibilitychange', async () => {
      if (window.document.hidden || this.networkState.stayOfflineMode()) return;
      try {
        await this.swUpdate.checkForUpdate();
      } catch (err) {
        this.logger.error('Update check failed on visibility change:', err);
      }
    });

    window.addEventListener('focus', async () => {
      if (this.networkState.stayOfflineMode()) return;
      try {
        await this.swUpdate.checkForUpdate();
      } catch (err) {
        this.logger.error('Update check failed on focus:', err);
      }
    });
  }

  // ── Version event handling ─────────────────────────────────────────────────

  private listenForVersionEvents(): void {
    this.swUpdate.versionUpdates.subscribe((event: VersionEvent) => {
      switch (event.type) {
        case 'VERSION_DETECTED':
          this.beacon(`📦 VERSION_DETECTED hash=${event.version.hash}`);
          this.logger.info('📦 SW: new version detected', { hash: event.version.hash });
          break;
        case 'VERSION_INSTALLATION_FAILED':
          this.beacon(
            `❌ VERSION_INSTALLATION_FAILED hash=${event.version.hash} error=${event.error}`,
          );
          this.logger.error('❌ SW: version installation failed', {
            hash: event.version.hash,
            error: event.error,
          });
          break;
        case 'NO_NEW_VERSION_DETECTED':
          this.logger.debug('✅ SW: already current', { hash: event.version.hash });
          break;
        case 'VERSION_READY': {
          const ready = event as VersionReadyEvent;
          this.beacon(
            `🚀 VERSION_READY current=${ready.currentVersion.hash} latest=${ready.latestVersion.hash}`,
          );
          this.logger.info('🚀 SW: new version ready — offering update to user', {
            current: ready.currentVersion.hash,
            latest: ready.latestVersion.hash,
          });
          this.offerUpdateToUser(ready);
          break;
        }
      }
    });
  }

  private offerUpdateToUser(event: VersionReadyEvent): void {
    if (this.hasShownUpdateToast) {
      this.logger.debug('SW: update toast already shown — skipping duplicate');
      return;
    }
    this.hasShownUpdateToast = true;

    this.toast.info(
      this.translate.translate('sw.update_available'),
      false, // don't auto-dismiss
      0, // no timeout
      [
        {
          label: this.translate.translate('sw.reload'),
          onClick: () => {
            this.logger.info('User accepted SW update — activating and reloading');
            this.activateUpdate();
          },
        },
        {
          label: this.translate.translate('sw.dismiss'),
          onClick: () => {
            this.logger.info('User dismissed SW update toast');
            this.hasShownUpdateToast = false;
          },
        },
      ],
    );
  }

  // ── Unrecoverable state ────────────────────────────────────────────────────

  /**
   * NGSW fires `unrecoverable` when it can't serve the app from cache — most
   * commonly after rapid deploys that leave the cached manifest inconsistent.
   *
   * Previously: automatically called window.location.reload() — caused silent
   * infinite reload loops when multiple deploys happened in quick succession.
   *
   * Now: shows a user-consent toast. The user controls when the reload happens.
   */
  private listenForUnrecoverableErrors(): void {
    this.swUpdate.unrecoverable.subscribe((event) => {
      this.beacon(`🚨 UNRECOVERABLE_STATE reason=${event.reason}`);
      this.logger.error('🚨 SW unrecoverable state — user consent required to reload', {
        reason: event.reason,
      });

      this.toast.error(this.translate.translate('sw.error_reload_required'), false, 0, [
        {
          label: this.translate.translate('sw.reload'),
          onClick: () => {
            this.logger.info('User accepted reload after SW unrecoverable state');
            reloadPage('user accepted SW unrecoverable state reload');
          },
        },
      ]);
    });
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  async activateUpdate(): Promise<void> {
    try {
      this.logger.info('Activating SW update…');
      await this.swUpdate.activateUpdate();
      reloadPage('SW update activated');
    } catch (err) {
      this.logger.error('Failed to activate SW update — reloading anyway:', err);
      reloadPage('SW activateUpdate failed — reloading anyway');
    }
  }

  async checkForUpdate(): Promise<boolean> {
    try {
      return await this.swUpdate.checkForUpdate();
    } catch (err) {
      this.logger.error('Manual update check failed:', err);
      return false;
    }
  }
}
